import React,{useState,useEffect} from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import {format,parseISO} from 'date-fns';

const {FiPlus,FiSearch,FiEdit,FiEye,FiEyeOff,FiCalendar,FiChevronLeft,FiChevronRight,FiChevronsLeft,FiChevronsRight,FiUsers,FiX,FiFilter}=FiIcons;

const Patients=()=> {
  const [patients,setPatients]=useState([]);
  const [loading,setLoading]=useState(true);
  const [searchTerm,setSearchTerm]=useState('');
  const [showModal,setShowModal]=useState(false);
  const [selectedPatient,setSelectedPatient]=useState(null);
  const [formData,setFormData]=useState({
    full_name: '',
    contact_number: '',
    email: '',
    national_id: '',
    date_of_birth: '',
    sex: '',
    nationality: '',
    address: ''
  });
  const [showFilter,setShowFilter]=useState(false);
  const [filter,setFilter]=useState({
    hasBalance: false
  });

  // Pagination state
  const [pagination,setPagination]=useState({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 20,
    totalItems: 0
  });
  const [allPatients,setAllPatients]=useState([]);
  const [filteredPatients,setFilteredPatients]=useState([]);
  const [isSearching,setIsSearching]=useState(false);

  const navigate=useNavigate();

  // List of countries for the dropdown
  const countries=[
    "Afghanistan","Albania","Algeria","Andorra","Angola","Antigua and Barbuda","Argentina","Armenia","Australia","Austria","Azerbaijan","Bahamas","Bahrain","Bangladesh","Barbados","Belarus","Belgium","Belize","Benin","Bhutan","Bolivia","Bosnia and Herzegovina","Botswana","Brazil","Brunei","Bulgaria","Burkina Faso","Burundi","Cabo Verde","Cambodia","Cameroon","Canada","Central African Republic","Chad","Chile","China","Colombia","Comoros","Congo","Costa Rica","Croatia","Cuba","Cyprus","Czech Republic","Denmark","Djibouti","Dominica","Dominican Republic","East Timor","Ecuador","Egypt","El Salvador","Equatorial Guinea","Eritrea","Estonia","Eswatini","Ethiopia","Fiji","Finland","France","Gabon","Gambia","Georgia","Germany","Ghana","Greece","Grenada","Guatemala","Guinea","Guinea-Bissau","Guyana","Haiti","Honduras","Hungary","Iceland","India","Indonesia","Iran","Iraq","Ireland","Israel","Italy","Jamaica","Japan","Jordan","Kazakhstan","Kenya","Kiribati","Korea, North","Korea, South","Kosovo","Kuwait","Kyrgyzstan","Laos","Latvia","Lebanon","Lesotho","Liberia","Libya","Liechtenstein","Lithuania","Luxembourg","Madagascar","Malawi","Malaysia","Maldives","Mali","Malta","Marshall Islands","Mauritania","Mauritius","Mexico","Micronesia","Moldova","Monaco","Mongolia","Montenegro","Morocco","Mozambique","Myanmar","Namibia","Nauru","Nepal","Netherlands","New Zealand","Nicaragua","Niger","Nigeria","North Macedonia","Norway","Oman","Pakistan","Palau","Palestine","Panama","Papua New Guinea","Paraguay","Peru","Philippines","Poland","Portugal","Qatar","Romania","Russia","Rwanda","Saint Kitts and Nevis","Saint Lucia","Saint Vincent and the Grenadines","Samoa","San Marino","Sao Tome and Principe","Saudi Arabia","Senegal","Serbia","Seychelles","Sierra Leone","Singapore","Slovakia","Slovenia","Solomon Islands","Somalia","South Africa","South Sudan","Spain","Sri Lanka","Sudan","Suriname","Sweden","Switzerland","Syria","Taiwan","Tajikistan","Tanzania","Thailand","Togo","Tonga","Trinidad and Tobago","Tunisia","Turkey","Turkmenistan","Tuvalu","Uganda","Ukraine","United Arab Emirates","United Kingdom","United States","Uruguay","Uzbekistan","Vanuatu","Vatican City","Venezuela","Vietnam","Yemen","Zambia","Zimbabwe"
  ];

  // Format Patient ID with leading zeroes
  const formatPatientId = (id) => {
    return String(id).padStart(6, '0');
  };

  useEffect(()=> {
    fetchPatients();
  },[]);

  useEffect(()=> {
    if (filter.hasBalance) {
      fetchPatients();
    }
  },[filter]);

  useEffect(()=> {
    // Update displayed patients when page changes or when filtered patients change
    updatePaginatedData();
  },[pagination.currentPage,filteredPatients]);

  // New effect to filter patients as user types
  useEffect(()=> {
    if (searchTerm.trim()==='') {
      // If search term is empty, reset to all patients
      setFilteredPatients(allPatients);
      setIsSearching(false);
    } else {
      // Filter patients based on search term
      setIsSearching(true);
      const searchTermLower=searchTerm.toLowerCase();
      const formatted = formatPatientId(searchTerm); // Format search term as patient ID
      const filtered=allPatients.filter(patient=> {
        return (
          (patient.full_name && patient.full_name.toLowerCase().includes(searchTermLower)) ||
          (patient.contact_number && patient.contact_number.toLowerCase().includes(searchTermLower)) ||
          (patient.email && patient.email.toLowerCase().includes(searchTermLower)) ||
          (patient.national_id && patient.national_id.toLowerCase().includes(searchTermLower)) ||
          (patient.nationality && patient.nationality.toLowerCase().includes(searchTermLower)) ||
          (patient.address && patient.address.toLowerCase().includes(searchTermLower)) ||
          // Search by Patient ID (formatted)
          formatPatientId(patient.id).includes(searchTerm) ||
          // Search by raw Patient ID
          patient.id.toString().includes(searchTerm)
        );
      });
      setFilteredPatients(filtered);
      // Reset to first page when searching
      setPagination(prev=> ({
        ...prev,
        currentPage: 1,
        totalPages: Math.ceil(filtered.length / prev.itemsPerPage),
        totalItems: filtered.length
      }));
    }
  },[searchTerm,allPatients]);

  const fetchPatients=async ()=> {
    try {
      setLoading(true);
      let url='/api/patients';
      if (filter.hasBalance) {
        url +='?hasBalance=true';
      }

      const response=await axios.get(url);
      
      // Store all patients for filtering
      setAllPatients(response.data);
      setFilteredPatients(response.data);
      
      // Calculate total pages
      const totalItems=response.data.length;
      const totalPages=Math.ceil(totalItems / pagination.itemsPerPage);
      
      // Update pagination info
      setPagination(prev=> ({
        ...prev,
        totalPages,
        totalItems,
        currentPage: prev.currentPage > totalPages ? 1 : prev.currentPage
      }));
      
      // Initial update of displayed patients
      setIsSearching(false);
    } catch (error) {
      console.error('Error fetching patients:',error);
    } finally {
      setLoading(false);
    }
  };

  const updatePaginatedData=()=> {
    const patientsData=filteredPatients;
    const {currentPage,itemsPerPage}=pagination;
    
    // Calculate start and end indices for current page
    const startIndex=(currentPage - 1) * itemsPerPage;
    const endIndex=startIndex + itemsPerPage;
    
    // Get patients for current page
    const paginatedPatients=patientsData.slice(startIndex,endIndex);
    setPatients(paginatedPatients);
  };

  const handlePageChange=(newPage)=> {
    setPagination(prev=> ({
      ...prev,
      currentPage: newPage
    }));
    // Scroll to top when page changes
    window.scrollTo({top: 0,behavior: 'smooth'});
  };

  const clearSearch=()=> {
    setSearchTerm('');
    setIsSearching(false);
    setFilteredPatients(allPatients);
    // Reset pagination
    setPagination(prev=> ({
      ...prev,
      currentPage: 1,
      totalPages: Math.ceil(allPatients.length / prev.itemsPerPage),
      totalItems: allPatients.length
    }));
  };

  const handleSubmit=async (e)=> {
    e.preventDefault();
    try {
      if (selectedPatient) {
        await axios.put(`/api/patients/${selectedPatient.id}`,formData);
      } else {
        await axios.post('/api/patients',formData);
      }
      setShowModal(false);
      setSelectedPatient(null);
      setFormData({
        full_name: '',
        contact_number: '',
        email: '',
        national_id: '',
        date_of_birth: '',
        sex: '',
        nationality: '',
        address: ''
      });
      fetchPatients();
    } catch (error) {
      console.error('Error saving patient:',error);
    }
  };

  const openModal=(patient=null)=> {
    setSelectedPatient(patient);
    if (patient) {
      // Format date of birth if it exists
      const formattedDateOfBirth=patient.date_of_birth 
        ? format(new Date(patient.date_of_birth),'yyyy-MM-dd') 
        : '';
      
      setFormData({
        ...patient,
        date_of_birth: formattedDateOfBirth
      });
    } else {
      setFormData({
        full_name: '',
        contact_number: '',
        email: '',
        national_id: '',
        date_of_birth: '',
        sex: '',
        nationality: '',
        address: ''
      });
    }
    setShowModal(true);
  };

  const viewAppointments=(patientId)=> {
    navigate(`/appointments?patient_id=${patientId}`);
  };

  const createAppointment=(patient)=> {
    navigate(`/appointments?new=true&patient_id=${patient.id}&patient_name=${patient.full_name}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-grey-900">Patients</h1>
        <button
          onClick={()=> openModal()}
          className="bg-maroon-600 hover:bg-maroon-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4" />
          <span>Add Patient</span>
        </button>
      </div>

      <div className="bg-white shadow rounded-lg">
        {/* Sticky Search Bar Section */}
        <div className="sticky top-0 z-10 bg-white border-b border-grey-200 shadow-sm">
          <div className="p-6">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1 relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-grey-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search patients by name, contact, email, ID, QID, or nationality..."
                  className="w-full pl-10 pr-10 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                  value={searchTerm}
                  onChange={(e)=> setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-grey-400 hover:text-grey-600"
                    onClick={clearSearch}
                  >
                    <SafeIcon icon={FiX} className="w-5 h-5" />
                  </button>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={()=> setShowFilter(!showFilter)}
                  className="bg-grey-200 hover:bg-grey-300 text-grey-800 px-4 py-2 rounded-lg transition-colors flex items-center space-x-1"
                >
                  <SafeIcon icon={FiFilter} className="w-4 h-4" />
                  <span>Filters</span>
                </button>
              </div>
            </div>

            <div className="mt-2 text-sm text-grey-600">
              {pagination.totalItems} patient{pagination.totalItems !== 1 ? 's' : ''} found
            </div>

            {showFilter && (
              <div className="mt-4 p-4 bg-grey-50 rounded-lg">
                <h3 className="text-sm font-medium text-grey-700 mb-2">Filters</h3>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      className="rounded border-grey-300 text-maroon-600 focus:ring-maroon-500"
                      checked={filter.hasBalance}
                      onChange={(e)=> setFilter({...filter,hasBalance: e.target.checked})}
                    />
                    <span className="text-sm text-grey-700">Has Balance Due</span>
                  </label>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-grey-200">
            <thead className="bg-grey-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Patient ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Patient
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  QID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Demographics
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-grey-200">
              {patients.map((patient)=> (
                <motion.tr
                  key={patient.id}
                  initial={{opacity: 0}}
                  animate={{opacity: 1}}
                  className="hover:bg-grey-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-grey-900">
                      #{formatPatientId(patient.id)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-grey-900">
                        {patient.full_name}
                      </div>
                      <div className="text-sm text-grey-500">{patient.email}</div>
                      {patient.balance > 0 && (
                        <div className="text-xs text-red-600 mt-1">
                          Balance: QR {patient.balance}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-900">
                    {patient.contact_number}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-grey-900">
                    {patient.national_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-grey-900">
                      <div>{patient.sex} • {patient.age || '--'} yrs</div>
                      <div className="text-xs text-grey-500">{patient.nationality}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-3">
                      <button
                        onClick={()=> openModal(patient)}
                        className="text-maroon-600 hover:text-maroon-900"
                        title="Edit Patient"
                      >
                        <SafeIcon icon={FiEdit} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={()=> viewAppointments(patient.id)}
                        className="text-blue-600 hover:text-blue-900"
                        title="View Appointments"
                      >
                        <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={()=> createAppointment(patient)}
                        className="text-green-600 hover:text-green-900"
                        title="Create Appointment"
                      >
                        <SafeIcon icon={FiPlus} className="w-4 h-4" />
                      </button>
                      {!!patient.hidden && (
                        <SafeIcon
                          icon={FiEyeOff}
                          className="w-4 h-4 text-grey-400"
                          title="Hidden Patient"
                        />
                      )}
                    </div>
                  </td>
                </motion.tr>
              ))}
              {patients.length===0 && (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center">
                    <div className="flex flex-col items-center">
                      <SafeIcon icon={FiUsers} className="w-12 h-12 text-grey-400 mb-4" />
                      <h3 className="text-lg font-medium text-grey-900 mb-2">No patients found</h3>
                      <p className="text-grey-600 mb-4">
                        {isSearching 
                          ? "No patients match your search criteria." 
                          : "Add your first patient to get started."
                        }
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 bg-grey-50 border-t border-grey-200 flex flex-wrap items-center justify-between">
            <div className="text-sm text-grey-700">
              Showing {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1} to{' '}
              {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
              {pagination.totalItems} patients
            </div>
            <div className="flex space-x-2 mt-2 sm:mt-0">
              <button
                onClick={()=> handlePageChange(1)}
                disabled={pagination.currentPage === 1}
                className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="First Page"
              >
                <SafeIcon icon={FiChevronsLeft} className="w-5 h-5" />
              </button>
              <button
                onClick={()=> handlePageChange(pagination.currentPage - 1)}
                disabled={pagination.currentPage === 1}
                className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Previous Page"
              >
                <SafeIcon icon={FiChevronLeft} className="w-5 h-5" />
              </button>
              <div className="flex items-center px-4">
                <span className="text-grey-700">Page {pagination.currentPage} of {pagination.totalPages}</span>
              </div>
              <button
                onClick={()=> handlePageChange(pagination.currentPage + 1)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Next Page"
              >
                <SafeIcon icon={FiChevronRight} className="w-5 h-5" />
              </button>
              <button
                onClick={()=> handlePageChange(pagination.totalPages)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="p-2 rounded-md border border-grey-300 bg-white text-grey-700 hover:bg-grey-50 disabled:opacity-50 disabled:cursor-not-allowed"
                title="Last Page"
              >
                <SafeIcon icon={FiChevronsRight} className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{scale: 0.95, opacity: 0}}
            animate={{scale: 1, opacity: 1}}
            className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-screen overflow-y-auto"
          >
            <h3 className="text-lg font-medium text-grey-900 mb-4">
              {selectedPatient ? 'Edit Patient' : 'Add New Patient'}
            </h3>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    required
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={formData.full_name}
                    onChange={(e)=> setFormData({...formData, full_name: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Contact Number
                  </label>
                  <input
                    type="tel"
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={formData.contact_number}
                    onChange={(e)=> setFormData({...formData, contact_number: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={formData.email}
                    onChange={(e)=> setFormData({...formData, email: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    QID
                  </label>
                  <input
                    type="text"
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={formData.national_id}
                    onChange={(e)=> setFormData({...formData, national_id: e.target.value})}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={formData.date_of_birth || ''}
                    onChange={(e)=> setFormData({...formData, date_of_birth: e.target.value})}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Sex
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={formData.sex || ''}
                    onChange={(e)=> setFormData({...formData, sex: e.target.value})}
                  >
                    <option value="">Select</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-grey-700 mb-1">
                    Nationality
                  </label>
                  <select
                    className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                    value={formData.nationality || ''}
                    onChange={(e)=> setFormData({...formData, nationality: e.target.value})}
                  >
                    <option value="">Select</option>
                    {countries.map((country)=> (
                      <option key={country} value={country}>
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-grey-700 mb-1">
                  Address
                </label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-grey-300 rounded-lg focus:ring-maroon-500 focus:border-maroon-500"
                  value={formData.address || ''}
                  onChange={(e)=> setFormData({...formData, address: e.target.value})}
                ></textarea>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={()=> setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-grey-300 text-grey-700 rounded-lg hover:bg-grey-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-maroon-600 text-white rounded-lg hover:bg-maroon-700 transition-colors"
                >
                  {selectedPatient ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Patients;